import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificacionesAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { isPushSupported, getCurrentSubscription, subscribePush, unsubscribePush } from '../utils/pushManager';
import './NotificacionesBell.css';

const TIPO_ICONOS = {
  reserva: '📅',
  cita: '📋',
  pago: '💰',
  cliente: '👤',
  factura: '🧾',
  sistema: '⚙️',
  info: 'ℹ️',
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Hace ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `Hace ${diffD}d`;
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

const POLL_INTERVAL = 60000;

function NotificacionesBell({ navbarTextColor }) {
  const [open, setOpen] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const pushSupported = isPushSupported();

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificacionesAPI.countNoLeidas();
      setNoLeidas(res.data?.count ?? 0);
    } catch (_) {}
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificacionesAPI.getAll(30, 0);
      setNotificaciones(res.data?.data ?? []);
      setNoLeidas(res.data?.no_leidas ?? 0);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (pushSupported) {
      getCurrentSubscription().then(sub => setPushEnabled(!!sub)).catch(() => {});
    }
  }, [pushSupported]);

  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribePush();
        setPushEnabled(false);
      } else {
        await subscribePush();
        setPushEnabled(true);
      }
    } catch (err) {
      const msg = err?.message || 'Error al gestionar notificaciones push';
      alert(msg);
    }
    setPushLoading(false);
  };

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleMarcarLeida = async (notif) => {
    if (!notif.leida) {
      await notificacionesAPI.marcarLeida(notif.id);
      setNotificaciones(prev => prev.map(n => n.id === notif.id ? { ...n, leida: 1 } : n));
      setNoLeidas(prev => Math.max(0, prev - 1));
    }
    if (notif.enlace) {
      setOpen(false);
      navigate(notif.enlace);
    }
  };

  const handleMarcarTodas = async () => {
    await notificacionesAPI.marcarTodasLeidas();
    setNotificaciones(prev => prev.map(n => ({ ...n, leida: 1 })));
    setNoLeidas(0);
  };

  return (
    <div className="notif-bell-container" ref={ref}>
      <button
        className="navbar-icon notif-bell-btn"
        onClick={() => setOpen(!open)}
        title="Notificaciones"
        style={{ color: navbarTextColor, position: 'relative' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {noLeidas > 0 && (
          <span className="notif-badge">{noLeidas > 99 ? '99+' : noLeidas}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <span className="notif-dropdown-title">Notificaciones</span>
            {noLeidas > 0 && (
              <button className="notif-mark-all" onClick={handleMarcarTodas}>
                Marcar todas como leídas
              </button>
            )}
          </div>
          <div className="notif-dropdown-body">
            {loading && notificaciones.length === 0 && (
              <div className="notif-empty">Cargando...</div>
            )}
            {!loading && notificaciones.length === 0 && (
              <div className="notif-empty">No hay notificaciones</div>
            )}
            {notificaciones.map(n => (
              <div
                key={n.id}
                className={`notif-item ${n.leida ? '' : 'notif-unread'}`}
                onClick={() => handleMarcarLeida(n)}
              >
                <span className="notif-item-icon">{TIPO_ICONOS[n.tipo] || TIPO_ICONOS.info}</span>
                <div className="notif-item-content">
                  <div className="notif-item-title">{n.titulo}</div>
                  {n.mensaje && <div className="notif-item-msg">{n.mensaje}</div>}
                  <div className="notif-item-time">{timeAgo(n.created_at)}</div>
                </div>
                {!n.leida && <span className="notif-dot" />}
              </div>
            ))}
          </div>
          {pushSupported && (
            <div className="notif-push-toggle">
              <label className="notif-push-label">
                <input
                  type="checkbox"
                  checked={pushEnabled}
                  onChange={handleTogglePush}
                  disabled={pushLoading}
                />
                <span>{pushLoading ? 'Procesando...' : 'Notificaciones push'}</span>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificacionesBell;
